// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import { API_URL } from "services/config";
import { formatMoneyPunto } from "utils/formatters";
import ModalPermisoPDF from "../PermisoPDF";
import ModalComparadorArchivos from "../ModalComparadorArchivos";
import ModalImage from "components/Modals/ModalImage";
import ModalInfraccionesSolicitante from "layouts/usuarios/components/ModalInfraccionesSolicitante";
import ModalDocumentacion from "../ModalDocumentacion";
import ModalPermisosCampo from "../ModalPermisosCampo";
import ModalPermisoAcompaniante from "../ModalPermisoAcompaniante";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalPermiso({ open, handleClose, idPermiso, refetch }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [documento, setDocumento] = useState();
  const [selectedImage, setSelectedImage] = useState(null);
  const [archivosSimilares, setArchivosSimilares] = useState([]);
  const inputRef = useRef();
  const [file, setFile] = useState();
  const { confirmar } = useContext(ConfirmarContext);
  const [openModalPermisoPDF, setOpenModalPermisoPDF] = useState(false);
  const [openModalInfracciones, setOpenModalInfracciones] = useState(false);
  const [openModalComparadorArchivos, setOpenModalComparadorArchivos] = useState(false);
  const [openModalDocumentacion, setOpenModalDocumentacion] = useState(false);
  const [openModalPermisosCampo, setOpenModalPermisosCampo] = useState(false);
  const [openModalPermisoAcompaniante, setOpenModalPermisoAcompaniante] = useState(false);

  const [getPermiso, { data, loading }] = useLazyQuery(
    gql`
      query getPermiso($id: ID!) {
        permisoGob(id: $id) {
          tipo
          estado
          fechaInicio
          motivoRechazo
          fechaVencimiento
          monto
          acompaniante
          usuario {
            id
            nombre
            apellido
            dni
            email
            cantidadInfracciones
            provincia {
              nombre
              id
            }
          }
          medioDePago
          comprobantesPago {
            id
            nombre
            file
            estado
            tipo
            createdAt
            motivoRechazo
            usuario {
              nombre
              apellido
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [updatePermisoStatus, { loading: loadingUpdate }] = useMutation(gql`
    mutation updatePermisoStatus($id: ID!, $estado: String!, $motivoRechazo: String) {
      updatePermisoStatus(id: $id, estado: $estado, motivoRechazo: $motivoRechazo) {
        id
      }
    }
  `);

  const [addPermisoFile, { loading: loadingFile }] = useMutation(gql`
    mutation addPermisoFile($idPermiso: ID!, $file: Upload!, $nombre: String!) {
      addPermisoFile(idPermiso: $idPermiso, file: $file, nombre: $nombre) {
        id
      }
    }
  `);

  const [verificarArchivosSimilares, { loading: loadingSimilares }] = useMutation(gql`
    mutation verificarArchivosSimilares($id: ID!) {
      verificarArchivosSimilares(id: $id) {
        similitud
        file {
          id
          nombre
          file
          usuario {
            nombre
            apellido
          }
          createdAt
          permiso {
            id
            anioPermiso
            nroPermiso
          }
        }
      }
    }
  `);

  useEffect(() => {
    setDocumento(null);
    setFile(null);
    setArchivosSimilares([]);
    if (idPermiso && open) {
      getPermiso({ variables: { id: idPermiso } });
    }
  }, [idPermiso, open]);

  useEffect(() => {
    if (
      data?.permisoGob?.estado === "En revisión" &&
      data?.permisoGob?.comprobantesPago?.find((c) => c.tipo === "Comprobante de pago")
    ) {
      verificarArchivosSimilares({
        variables: {
          id: data?.permisoGob?.comprobantesPago?.find((c) => c.tipo === "Comprobante de pago")?.id,
        },
      }).then((res) => {
        setArchivosSimilares(res.data.verificarArchivosSimilares);
      });
    }
  }, [data]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Detalles del permiso</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox>
              <Grid container spacing={2}>
                {data?.permisoGob?.usuario?.cantidadInfracciones > 0 &&
                  data?.permisoGob?.estado === "En revisión" && (
                    <Grid item xs={12}>
                      <Card>
                        <SoftBox
                          bgColor="error"
                          p={3}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          justifyContent="center"
                          onClick={() => setOpenModalInfracciones(true)}
                          sx={{
                            cursor: "pointer",
                          }}
                        >
                          <Icon
                            sx={{
                              color: "#fff",
                            }}
                          >
                            warning
                          </Icon>
                          <SoftTypography variant="h6" color="white">
                            {`El solicitante tiene ${data?.permisoGob?.usuario?.cantidadInfracciones} infracciones`}
                          </SoftTypography>
                        </SoftBox>
                      </Card>
                    </Grid>
                  )}
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Solicitante
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">{`${data?.permisoGob?.usuario?.nombre} ${data?.permisoGob?.usuario?.apellido}`}</SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      DNI del solicitante
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">
                    {formatMoneyPunto(data?.permisoGob?.usuario?.dni)}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Email del solicitante
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">{data?.permisoGob?.usuario?.email}</SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha de inicio
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">
                    {dayjs(data?.permisoGob?.fechaInicio).format("DD/MM/YYYY")}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha de vencimiento
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">
                    {dayjs(data?.permisoGob?.fechaVencimiento).format("DD/MM/YYYY")}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Tipo de permiso
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">{data?.permisoGob?.tipo}</SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Medio de pago
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">{data?.permisoGob?.medioDePago}</SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Provincia
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">
                    {data?.permisoGob?.usuario?.provincia?.nombre}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaPermiso">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Monto a abonar
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6">{`$${formatMoneyPunto(
                    data?.permisoGob?.monto
                  )} ARS ${
                    data?.permisoGob?.acompaniante ? "(con acompañante)" : ""
                  }`}</SoftTypography>
                </Grid>
                {data?.permisoGob?.estado === "Rechazado" && (
                  <Grid item xs={12}>
                    <InputLabel htmlFor="fechaPermiso">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Motivo del rechazo
                      </SoftTypography>
                    </InputLabel>
                    <SoftTypography variant="h6">{data?.permisoGob?.motivoRechazo}</SoftTypography>
                  </Grid>
                )}
                {data?.permisoGob?.estado !== "Pendiente de pago" && (
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Ver permisos de campo" placement="top">
                      <SoftButton
                        color="gobSecondary"
                        fullWidth
                        onClick={() => {
                          setOpenModalPermisosCampo(true);
                        }}
                      >
                        Permisos de campo&nbsp;
                        <Icon>file_present</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                )}
                {data?.permisoGob?.estado !== "Pendiente de pago" && (
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Ver documentación" placement="top">
                      <SoftButton
                        color="gobSecondary"
                        fullWidth
                        onClick={() => {
                          setOpenModalDocumentacion(true);
                        }}
                      >
                        Documentación&nbsp;
                        <Icon>description</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                )}
                {data?.permisoGob?.comprobantesPago?.find(
                  (c) => c.tipo === "Comprobante de pago acompañante"
                ) && (
                  <Grid item xs={12} sm={12}>
                    <Tooltip title="Ver permiso de acompañante" placement="top">
                      <SoftButton
                        color="primary"
                        fullWidth
                        onClick={() => {
                          setOpenModalPermisoAcompaniante(true);
                        }}
                      >
                        Permiso de acompañante&nbsp;
                        <Icon>people_alt</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                )}
                {data?.permisoGob?.comprobantesPago?.find(
                  (c) => c.tipo === "Comprobante de pago"
                ) ? (
                  <Grid item xs={12}>
                    <SoftButton
                      color="primary"
                      fullWidth
                      onClick={() => {
                        if (
                          data?.permisoGob?.comprobantesPago
                            ?.find((c) => c.tipo === "Comprobante de pago")
                            .file.includes(".pdf")
                        ) {
                          window.open(
                            `${API_URL}/${
                              data?.permisoGob?.comprobantesPago?.find(
                                (c) => c.tipo === "Comprobante de pago"
                              ).file
                            }`,
                            "_blank"
                          );
                        } else {
                          setSelectedImage(
                            `${API_URL}/${
                              data?.permisoGob?.comprobantesPago?.find(
                                (c) => c.tipo === "Comprobante de pago"
                              ).file
                            }` || null
                          );
                        }
                      }}
                    >
                      Ver comprobante de pago &nbsp;
                      <Icon>arrow_forward</Icon>
                    </SoftButton>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <SoftButton
                      color={file ? "success" : "gobSecondary"}
                      fullWidth
                      onClick={() => inputRef.current.click()}
                      disabled={loadingFile}
                    >
                      {file
                        ? file.name.length > 40
                          ? `${file.name.substring(0, 40)}...${file.name.substring(
                              file.name.length - 4
                            )}`
                          : file.name
                        : "Subir comprobante de pago"}
                      &nbsp;
                      <Icon sx={{ fontWeight: "light" }}>cloud_upload</Icon>
                    </SoftButton>
                    <input
                      ref={inputRef}
                      type="file"
                      style={{ display: "none" }}
                      accept=".pdf, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        if (e.target.files[0] && e.target.files[0].size < 5000000) {
                          setFile(e.target.files[0]);
                        } else {
                          handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                        }
                      }}
                    />
                  </Grid>
                )}
                {data?.permisoGob?.estado === "En revisión" && (
                  <Grid item xs={12}>
                    <Tooltip
                      title={
                        loadingSimilares
                          ? "Verificando archivos similares..."
                          : archivosSimilares.length > 0
                          ? `Comparar archivos similares`
                          : "No hay archivos similares para comparar"
                      }
                      placement="top"
                    >
                      <SoftButton
                        color={
                          loadingSimilares
                            ? "gobSecondary"
                            : archivosSimilares.length > 0
                            ? "error"
                            : "success"
                        }
                        fullWidth
                        variant="text"
                        onClick={() => {
                          if (archivosSimilares.length > 0) {
                            setOpenModalComparadorArchivos(true);
                          }
                        }}
                      >
                        {loadingSimilares
                          ? "Verificando archivos similares..."
                          : archivosSimilares.length > 0
                          ? `Se encontraron ${archivosSimilares.length} archivos similares`
                          : "No hay archivos similares"}
                        &nbsp;
                        {loadingSimilares ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Icon>{archivosSimilares.length > 0 ? "warning" : "check"}</Icon>
                        )}
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </SoftBox>
            <Collapse
              in={
                (data?.permisoGob?.comprobantesPago?.find(
                  (c) => c.tipo === "Comprobante de pago"
                ) ||
                  file) &&
                data?.permisoGob?.estado !== "Aprobado"
              }
            >
              <Grid container spacing={2} pt={2}>
                {data?.permisoGob?.estado !== "Rechazado" && (
                  <Grid item xs={12} sm={6}>
                    <SoftButton
                      color="error"
                      fullWidth
                      disabled={
                        (!data?.permisoGob?.comprobantesPago?.find(
                          (c) => c.tipo === "Comprobante de pago"
                        ) &&
                          !file) ||
                        loadingUpdate
                      }
                      onClick={() => {
                        confirmar({
                          title: "Rechazar permiso",
                          pedirMotivo: true,
                          message: (
                            <SoftTypography variant="h6" fontWeight="light">
                              ¿Está seguro que desea rechazar el{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.tipo}`}</span>{" "}
                              de{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.usuario?.nombre} ${data?.permisoGob?.usuario?.apellido}`}</span>
                              ?
                            </SoftTypography>
                          ),
                          icon: "warning",
                          func: (motivo) => {
                            updatePermisoStatus({
                              variables: {
                                id: idPermiso,
                                estado: "Rechazado",
                                motivoRechazo: motivo,
                              },
                            })
                              .then(() => {
                                handleSnackbar("Permiso aprobado correctamente", "success");
                                refetch();
                                handleClose();
                              })
                              .catch(() => {
                                handleSnackbar("Error al aprobar el permiso", "error");
                              });
                          },
                        });
                      }}
                    >
                      Rechazar&nbsp;
                      <Icon>close</Icon>
                    </SoftButton>
                  </Grid>
                )}
                <Grid item xs={12} sm={data?.permisoGob?.estado === "Rechazado" ? 12 : 6}>
                  <SoftButton
                    color="success"
                    fullWidth
                    disabled={
                      (!data?.permisoGob?.comprobantesPago?.find(
                        (c) => c.tipo === "Comprobante de pago"
                      ) &&
                        !file) ||
                      loadingUpdate
                    }
                    onClick={() => {
                      if (
                        data?.permisoGob?.comprobantesPago?.find(
                          (c) => c.tipo === "Permiso de campo" && c.estado === "Pendiente "
                        )
                      ) {
                        confirmar({
                          title: "Aprobar permiso",
                          message: (
                            <SoftTypography variant="h6" fontWeight="light">
                              ¿Está seguro que desea generar un{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.tipo}`}</span>{" "}
                              para{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.usuario?.nombre} ${data?.permisoGob?.usuario?.apellido}`}</span>
                              ? <br />
                              Hay un permisos de campo pendientes de revisión.
                            </SoftTypography>
                          ),
                          icon: "warning",
                          func: () => {
                            if (file) {
                              addPermisoFile({
                                variables: {
                                  idPermiso,
                                  file: file,
                                  nombre: "Comprobante de pago",
                                },
                              })
                                .then(() => {
                                  updatePermisoStatus({
                                    variables: {
                                      id: idPermiso,
                                      estado: "Aprobado",
                                    },
                                  })
                                    .then(() => {
                                      handleSnackbar("Permiso aprobado correctamente", "success");
                                      refetch();
                                      handleClose();
                                    })
                                    .catch(() => {
                                      handleSnackbar("Error al aprobar el permiso", "error");
                                    });
                                })
                                .catch(() => {
                                  handleSnackbar("Error al subir el comprobante de pago", "error");
                                });
                            } else {
                              updatePermisoStatus({
                                variables: {
                                  id: idPermiso,
                                  estado: "Aprobado",
                                },
                              })
                                .then(() => {
                                  handleSnackbar("Permiso aprobado correctamente", "success");
                                  refetch();
                                  handleClose();
                                })
                                .catch(() => {
                                  handleSnackbar("Error al aprobar el permiso", "error");
                                });
                            }
                          },
                        });
                      } else {
                        confirmar({
                          title: "Aprobar permiso",
                          message: (
                            <SoftTypography variant="h6" fontWeight="light">
                              ¿Está seguro que desea generar un{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.tipo}`}</span>{" "}
                              para{" "}
                              <span
                                style={{ fontWeight: "bold" }}
                              >{`${data?.permisoGob?.usuario?.nombre} ${data?.permisoGob?.usuario?.apellido}`}</span>
                              ?
                            </SoftTypography>
                          ),
                          icon: "warning",
                          func: () => {
                            if (file) {
                              addPermisoFile({
                                variables: {
                                  idPermiso,
                                  file: file,
                                  nombre: "Comprobante de pago",
                                },
                              })
                                .then(() => {
                                  updatePermisoStatus({
                                    variables: {
                                      id: idPermiso,
                                      estado: "Aprobado",
                                    },
                                  })
                                    .then(() => {
                                      handleSnackbar("Permiso aprobado correctamente", "success");
                                      refetch();
                                      handleClose();
                                    })
                                    .catch(() => {
                                      handleSnackbar("Error al aprobar el permiso", "error");
                                    });
                                })
                                .catch(() => {
                                  handleSnackbar("Error al subir el comprobante de pago", "error");
                                });
                            } else {
                              updatePermisoStatus({
                                variables: {
                                  id: idPermiso,
                                  estado: "Aprobado",
                                },
                              })
                                .then(() => {
                                  handleSnackbar("Permiso aprobado correctamente", "success");
                                  refetch();
                                  handleClose();
                                })
                                .catch(() => {
                                  handleSnackbar("Error al aprobar el permiso", "error");
                                });
                            }
                          },
                        });
                      }
                    }}
                  >
                    Aprobar&nbsp;
                    <Icon>check</Icon>
                  </SoftButton>
                </Grid>
              </Grid>
            </Collapse>
            <Collapse in={data?.permisoGob?.estado === "Aprobado"}>
              <Grid container spacing={2} pt={2}>
                <Grid item xs={12}>
                  <SoftButton
                    color="gobSecondary"
                    fullWidth
                    onClick={() => {
                      setOpenModalPermisoPDF(true);
                    }}
                  >
                    Ver permiso&nbsp;
                    <Icon>file_open</Icon>
                  </SoftButton>
                </Grid>
              </Grid>
            </Collapse>
          </SoftBox>
          <ModalPermisoPDF
            open={openModalPermisoPDF}
            handleClose={() => setOpenModalPermisoPDF(false)}
            idPermiso={idPermiso}
          />
          <ModalInfraccionesSolicitante
            open={openModalInfracciones}
            idSolicitante={data?.permisoGob?.usuario?.id}
            handleClose={() => setOpenModalInfracciones(false)}
          />
          <ModalComparadorArchivos
            open={openModalComparadorArchivos}
            handleClose={() => setOpenModalComparadorArchivos(false)}
            archivoOriginal={data?.permisoGob?.comprobantesPago?.find(
              (c) => c.tipo === "Comprobante de pago"
            )}
            archivosSimilares={archivosSimilares}
          />
          <ModalDocumentacion
            open={openModalDocumentacion}
            handleClose={() => setOpenModalDocumentacion(false)}
            archivos={
              data?.permisoGob?.comprobantesPago?.filter((c) => c.tipo !== "Permiso de campo") || []
            }
          />
          <ModalPermisosCampo
            open={openModalPermisosCampo}
            handleClose={() => setOpenModalPermisosCampo(false)}
            archivos={
              data?.permisoGob?.comprobantesPago?.filter((c) => c.tipo === "Permiso de campo") || []
            }
            refetch={() => getPermiso({ variables: { id: idPermiso } })}
          />
          <ModalPermisoAcompaniante
            open={openModalPermisoAcompaniante}
            handleClose={() => setOpenModalPermisoAcompaniante(false)}
            archivos={
              data?.permisoGob?.comprobantesPago?.filter(
                (c) => c.tipo === "Comprobante de pago acompañante"
              ) || []
            }
            refetch={() => getPermiso({ variables: { id: idPermiso } })}
            idPermiso={idPermiso}
          />
          <ModalImage
            open={selectedImage !== null}
            handleClose={() => setSelectedImage(null)}
            src={selectedImage}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalPermiso.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPermiso: PropTypes.string,
  refetch: PropTypes.func,
};
